import React from 'react';

// Full-screen loading state — animated Routemind logo mark + wordmark.
// Used while auth state is resolving on refresh, and reusable elsewhere.
const LoadingScreen = () => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      background: '#0B1929',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      fontFamily: "'DM Sans', sans-serif",
    }}
  >
    <svg width={140} height={140} viewBox="-30 -30 60 60" style={{ overflow: 'visible' }}>
      {/* Scattered nodes converging */}
      <circle fill="#1D9E75" style={{ animation: 'scatter1 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter2 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter3 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter4 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter5 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter6 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter7 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle fill="#1D9E75" style={{ animation: 'scatter8 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>

      <g style={{ animation: 'lineReveal 12s cubic-bezier(0.4,0,0.2,1) infinite' }}>
        <line x1="-12" y1="-11" x2="8" y2="-13" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="-12" y1="-11" x2="-9" y2="8" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="-12" y1="-11" x2="0" y2="0" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="8" y1="-13" x2="14" y2="-1" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="8" y1="-13" x2="0" y2="0" stroke="#1D9E75" strokeWidth="0.8"/>
      </g>
      <g style={{ animation: 'lineReveal2 12s cubic-bezier(0.4,0,0.2,1) infinite' }}>
        <line x1="14" y1="-1" x2="0" y2="0" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="14" y1="-1" x2="9" y2="12" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="-9" y1="8" x2="0" y2="0" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="-9" y1="8" x2="9" y2="12" stroke="#1D9E75" strokeWidth="0.8"/>
        <line x1="9" y1="12" x2="0" y2="0" stroke="#1D9E75" strokeWidth="0.8"/>
      </g>
      <g style={{ animation: 'lineReveal3 12s cubic-bezier(0.4,0,0.2,1) infinite' }}>
        <line x1="-17" y1="2" x2="-9" y2="8" stroke="#1D9E75" strokeWidth="0.6"/>
        <line x1="-17" y1="2" x2="-12" y2="-11" stroke="#1D9E75" strokeWidth="0.6"/>
        <line x1="16" y1="16" x2="9" y2="12" stroke="#1D9E75" strokeWidth="0.6"/>
        <line x1="-5" y1="-18" x2="-12" y2="-11" stroke="#1D9E75" strokeWidth="0.6"/>
        <line x1="-5" y1="-18" x2="8" y2="-13" stroke="#1D9E75" strokeWidth="0.6"/>
      </g>

      <g style={{ animation: 'routeReveal 12s cubic-bezier(0.4,0,0.2,1) infinite' }}>
        <path className="flow" d="M-12,-11 Q-4,-16 8,-13" stroke="#D85A30" strokeWidth="0.7" fill="none" style={{ animationDelay: '0.5s' }}/>
        <path className="flow" d="M14,-1 Q15,8 9,12" stroke="#D85A30" strokeWidth="0.7" fill="none" style={{ animationDelay: '1.5s' }}/>
        <path className="flow" d="M-9,8 Q-2,14 9,12" stroke="#D85A30" strokeWidth="0.7" fill="none" style={{ animationDelay: '2.5s' }}/>
      </g>

      <circle cx="0" cy="0" fill="none" stroke="#D85A30" strokeWidth="0.5" style={{ animation: 'ringGrow 12s cubic-bezier(0.4,0,0.2,1) infinite' }}/>
      <circle cx="0" cy="0" fill="#D85A30" style={{ animation: 'coreBreathe 12s ease-in-out infinite' }}/>

      <g>
        <g style={{ animation: 'iconAir 12s ease-in-out infinite' }}>
          <path d="M0,-3.8 Q0.4,-3.5 0.5,-2 L0.5,2.5 Q0.4,3.2 0,3.5 Q-0.4,3.2 -0.5,2.5 L-0.5,-2 Q-0.4,-3.5 0,-3.8 Z" fill="#ffffff" opacity="0.9"/>
          <path d="M-0.3,-0.3 L-4.2,0.5 L-4.2,1 L-0.3,0.3 Z" fill="#ffffff" opacity="0.9"/>
          <path d="M0.3,-0.3 L4.2,0.5 L4.2,1 L0.3,0.3 Z" fill="#ffffff" opacity="0.9"/>
          <path d="M-0.3,2.2 L-2,2.8 L-2,3.1 L-0.3,2.6 Z" fill="#ffffff" opacity="0.9"/>
          <path d="M0.3,2.2 L2,2.8 L2,3.1 L0.3,2.6 Z" fill="#ffffff" opacity="0.9"/>
          <path d="M0,1.8 L0,3.5 L0.15,3.2 L0.15,2 Z" fill="#ffffff" opacity="0.7"/>
        </g>
        <g style={{ animation: 'iconSea 12s ease-in-out infinite' }}>
          <path d="M-3.5,0 L-2.5,-2.5 L2.5,-2.5 L3.5,0 Z" stroke="#ffffff" strokeWidth="0.8" fill="none" strokeLinejoin="round"/>
          <rect x="-1" y="-2.5" width="2" height="-1.8" rx="0.2" stroke="#ffffff" strokeWidth="0.7" fill="none"/>
          <line x1="0" y1="-4.3" x2="0" y2="-2.5" stroke="#ffffff" strokeWidth="0.6" strokeLinecap="round"/>
          <path d="M-4,1 Q-2.5,0.3 -1,1 Q0.5,1.7 2,1 Q3.5,0.3 4,1" stroke="#ffffff" strokeWidth="0.6" fill="none" strokeLinecap="round"/>
          <path d="M-3.5,2 Q-2,1.3 -0.5,2 Q1,2.7 2.5,2 Q3.5,1.5 4,2" stroke="#ffffff" strokeWidth="0.45" fill="none" strokeLinecap="round" opacity="0.7"/>
        </g>
        <g style={{ animation: 'iconTruck 12s ease-in-out infinite' }}>
          <rect x="-4" y="-2.2" width="5.2" height="3.5" rx="0.4" stroke="#ffffff" strokeWidth="0.9" fill="none"/>
          <path d="M1.2,-0.8 L1.2,-2.2 L3.2,-2.2 L4.2,-0.8 L4.2,1.3 L1.2,1.3 Z" stroke="#ffffff" strokeWidth="0.7" fill="none" strokeLinejoin="round"/>
          <circle cx="-1.8" cy="2" r="0.9" stroke="#ffffff" strokeWidth="0.7" fill="none"/>
          <circle cx="3" cy="2" r="0.9" stroke="#ffffff" strokeWidth="0.7" fill="none"/>
          <line x1="-4" y1="1.3" x2="1.2" y2="1.3" stroke="#ffffff" strokeWidth="0.5"/>
        </g>
      </g>
    </svg>

    <div style={{ marginTop: 20, fontSize: 22, fontWeight: 500, letterSpacing: '-0.01em' }}>
      <span style={{ color: '#fff' }}>Route</span>
      <span style={{ color: '#9FE1CB' }}>mind</span>
    </div>
  </div>
);

export default LoadingScreen;
